import { NgClass } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { AdaptButtonModule } from "@common/ux/button/button.module";
import { AdaptLoadingSpinnerModule } from "@common/ux/loading-spinner/loading-spinner.module";
import uFuzzy from "@leeoniya/ufuzzy";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import { DxDropDownBoxComponent, DxScrollViewComponent, DxTemplateDirective, DxTextBoxComponent } from "devextreme-angular";
import { DxoDropDownBoxFieldAddonsComponent } from "devextreme-angular/ui/drop-down-box";
import { DxoDropDownOptionsComponent } from "devextreme-angular/ui/nested";
import { IIcon, IIconCategory, IIconData, IIconPickerGroup } from "./icon-picker.interface";
import { IconPickerDataService } from "./icon-picker-data.service";

@Component({
    selector: "adapt-icon-picker",
    templateUrl: "./icon-picker.component.html",
    styleUrl: "./icon-picker.component.css",
    imports: [
        AdaptButtonModule,
        AdaptLoadingSpinnerModule,
        DxDropDownBoxComponent,
        DxScrollViewComponent,
        DxTemplateDirective,
        DxTextBoxComponent,
        DxoDropDownBoxFieldAddonsComponent,
        DxoDropDownOptionsComponent,
        NgClass,
    ],
})
export class IconPickerComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() public value?: string;
    @Output() public valueChange = new EventEmitter<string>();

    @Input() public disabled = false;

    @Input() public iconStyle: "far" | "fal" | "fas" = "fas";
    @Input() public showCategories = true;
    @Input() public confirmIconSelection = false;

    @ViewChild("iconsList") public iconsList?: ElementRef<HTMLDivElement>;
    @ViewChild("iconPickerContent") public iconPickerContentContainer?: ElementRef<HTMLDivElement>;

    public loading = true;
    public filteredGroups: IIconPickerGroup[] = [];
    public icons: IIcon[] = [];
    public categories: IIconCategory[] = [];
    private iconToCategoryMap = new Map<string, string[]>();

    public searchTerm = "";
    private fuzzySearch = new uFuzzy();
    private iconHaystack: string[] = [];

    public popupVisible = false;
    public selectedValue?: string;

    public activeCategory?: string;
    private isManualScrolling = false;
    private searchUpdater = this.createThrottledUpdater<string>(() => this.updateFilteredGroups(this.getFilteredIconGroups()), 200);
    private categoryScrollObserver?: IntersectionObserver;
    private intersectingHeaders = new Map<string, Element>();

    public tooltipVisible = false;
    public tooltipText = "";
    public tooltipTransform = "";
    private lastMousePosition?: { x: number, y: number };
    private tooltipTimeout?: ReturnType<typeof setTimeout>;
    private tooltipAnimationRequest?: ReturnType<typeof requestAnimationFrame>;
    private scrollAnimationRequest?: ReturnType<typeof requestAnimationFrame>;
    private pendingTooltipTarget?: HTMLElement | null;

    constructor(private dataService: IconPickerDataService) {
        super();
    }

    public ngOnInit() {
        const skeleton = this.createPlaceholderIconData();
        this.setIconData(skeleton);

        this.dataService.getIconData()
            .pipe(this.takeUntilDestroyed())
            .subscribe((data) => {
                this.setIconData(data);
            });
    }
    
    public ngOnDestroy() {
        this.cancelTooltipAnimationRequests();
        this.disconnectCategoryScrollObserver();
        super.ngOnDestroy();
    }

    public onOpened() {
        // set the initial state for the popup
        this.searchTerm = "";
        this.selectedValue = this.value;
        this.activeCategory = this.filteredGroups[0]?.category.name;

        setTimeout(() => {
            this.loading = false;

            requestAnimationFrame(() => {
                this.initCategoryScrollObserver();

                if (this.value) {
                    this.scrollToIcon(this.value, "instant");
                }
            });
        }, 200);
    }

    public onClosed() {
        this.tooltipVisible = false;
        this.tooltipText = "";
        this.tooltipTransform = "";
        this.lastMousePosition = undefined;
        this.loading = true;
        this.cancelTooltipAnimationRequests();
        this.disconnectCategoryScrollObserver();
    }

    public onSearchTermChange(term: string) {
        this.searchTerm = term;
        this.searchUpdater.next(term);
    }

    public selectIcon(icon: IIcon) {
        if (this.confirmIconSelection) {
            this.selectedValue = icon.name;
        } else {
            this.changeValue(icon.name);
        }
    }

    public onValueChanged(event: ValueChangedEvent) {
        // cleared field
        if (!event.value) {
            this.changeValue(undefined);
        }
    }

    public onConfirm() {
        this.changeValue(this.selectedValue);
    }

    public onCancel() {
        this.popupVisible = false;
    }

    public scrollToCategory(category: IIconCategory) {
        this.isManualScrolling = true;

        this.activeCategory = category.name;
        const element = document.getElementById(`category-${category.name}`);
        element?.scrollIntoView({ behavior: "smooth", block: "start" });

        // Reset manual scrolling flag after the smooth scroll is likely finished
        setTimeout(() => this.isManualScrolling = false, 1_500);
    }

    public handleMouseMove(event: MouseEvent) {
        this.lastMousePosition = { x: event.clientX, y: event.clientY };
        if (event.target) {
            this.scheduleTooltipUpdate(event.target as HTMLElement | null);
        }
    }

    public handleMouseLeave() {
        this.hideTooltip();
    }

    public onScroll() {
        if (this.scrollAnimationRequest) {
            return;
        }

        this.scrollAnimationRequest = requestAnimationFrame(() => {
            this.scrollAnimationRequest = undefined;
            if (this.lastMousePosition) {
                const target = document.elementFromPoint(this.lastMousePosition.x, this.lastMousePosition.y);
                if (target) {
                    this.scheduleTooltipUpdate(target as HTMLElement | null);
                }
            }
        });
    }

    private updateFilteredGroups(groups: IIconPickerGroup[]) {
        this.filteredGroups = groups;

        if (this.popupVisible) {
            setTimeout(() => {
                if (this.categoryScrollObserver) {
                    this.intersectingHeaders.clear();
                    this.observeCategoryHeaders();
                }
            });
        }
    }

    private setIconData({ icons, categories }: IIconData) {
        this.icons = icons;
        this.categories = categories;
        this.buildCaches();
        this.updateFilteredGroups(this.getFilteredIconGroups());
    }

    private buildCaches() {
        this.iconHaystack = this.icons.map((i) => `${i.name} ${i.searchTerms?.join(" ") || ""}`.toLowerCase());

        this.iconToCategoryMap.clear();
        for (const category of this.categories) {
            for (const iconName of category.icons) {
                const names = this.iconToCategoryMap.get(iconName);
                if (names) {
                    names.push(category.name);
                } else {
                    this.iconToCategoryMap.set(iconName, [category.name]);
                }
            }
        }
    }

    private createPlaceholderIconData(): IIconData {
        if (this.value) {
            return {
                icons: [{ name: this.value, label: this.value }],
                categories: [{ name: "skeleton", label: "", icon: this.value, icons: [this.value] }],
            };
        }
        return { icons: [], categories: [] };
    }

    private getFilteredIconGroups(): IIconPickerGroup[] {
        let filteredIcons = this.icons;

        if (this.searchTerm) {
            const [matchIndexes] = this.fuzzySearch.search(this.iconHaystack, this.searchTerm.toLowerCase());
            filteredIcons = matchIndexes?.map((idx) => filteredIcons[idx]) ?? [];
        }

        if (this.showCategories) {
            return this.categories
                .map((category) => ({
                    category,
                    icons: filteredIcons.filter((icon) => {
                        const catNames = this.iconToCategoryMap.get(icon.name);
                        return catNames?.includes(category.name);
                    }),
                }))
                .filter((group) => group.icons.length > 0);
        }

        return [{
            category: { name: "", label: "", icon: "", icons: [] },
            icons: filteredIcons,
        }];
    }

    private changeValue(value?: string) {
        this.value = value;
        this.valueChange.emit(this.value);
        this.popupVisible = false;
    }

    private scrollToIcon(iconName: string, behavior: ScrollBehavior = "smooth") {
        const element = document.getElementById(`icon-${iconName}`);
        element?.scrollIntoView({ behavior, block: "center" });
    }

    private initCategoryScrollObserver() {
        this.disconnectCategoryScrollObserver();

        const container = this.iconsList?.nativeElement;
        if (!container) {
            return;
        }

        const scrollContainer = container.closest(".dx-scrollable-container");

        this.categoryScrollObserver = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const name = (entry.target as HTMLElement).dataset.categoryName;
                if (!name) {
                    continue;
                }

                if (entry.isIntersecting) {
                    this.intersectingHeaders.set(name, entry.target);
                } else {
                    this.intersectingHeaders.delete(name);
                }
            }

            this.updateActiveCategoryFromScrollObserver();
        }, {
            root: scrollContainer,
            // extra margin to account for the header heights
            rootMargin: "-10px 0px 0px 0px",
            threshold: 0,
        });

        this.observeCategoryHeaders();
    }

    private updateActiveCategoryFromScrollObserver() {
        if (this.intersectingHeaders.size === 0 || this.isManualScrolling) {
            return;
        }

        let topCategory: string | undefined;
        let topOffset = Infinity;

        for (const [name, header] of this.intersectingHeaders) {
            const rect = header.getBoundingClientRect();
            if (rect.top < topOffset) {
                topOffset = rect.top;
                topCategory = name;
            }
        }

        if (topCategory && topCategory !== this.activeCategory) {
            this.activeCategory = topCategory;
            const sidebarIcon = document.getElementById(`category-icon-${topCategory}`);
            sidebarIcon?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }

    private observeCategoryHeaders() {
        if (!this.iconsList?.nativeElement || !this.categoryScrollObserver) {
            return;
        }

        const headers = this.iconsList.nativeElement.querySelectorAll(".category-header");
        headers.forEach((header) => this.categoryScrollObserver!.observe(header));
    }

    private disconnectCategoryScrollObserver() {
        this.categoryScrollObserver?.disconnect();
        this.categoryScrollObserver = undefined;
        this.intersectingHeaders.clear();
    }

    private scheduleTooltipUpdate(target: HTMLElement | null) {
        this.pendingTooltipTarget = target;
        if (this.tooltipAnimationRequest) {
            return;
        }

        this.tooltipAnimationRequest = requestAnimationFrame(() => {
            this.tooltipAnimationRequest = undefined;
            if (this.pendingTooltipTarget) {
                this.updateTooltipForElement(this.pendingTooltipTarget);
            } else {
                this.hideTooltip();
            }
        });
    }

    private updateTooltipForElement(target: HTMLElement) {
        const iconItem = target.closest("[data-label]") as HTMLElement;
        if (!iconItem) {
            this.hideTooltip();
            return;
        }

        const label = iconItem.dataset.label;
        const contentContainer = this.iconPickerContentContainer?.nativeElement;
        if (label && contentContainer) {
            this.tooltipText = label;
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
                this.tooltipTimeout = undefined;
            }

            const rect = iconItem.getBoundingClientRect();
            const containerRect = contentContainer.getBoundingClientRect();

            // Position tooltip above the icon
            const x = rect.left - containerRect.left + (rect.width / 2);
            const y = rect.top - containerRect.top - 4;

            this.tooltipTransform = `translate(calc(-50% + ${x}px), calc(-100% + ${y}px))`;

            this.tooltipVisible = true;
        }
    }

    private cancelTooltipAnimationRequests() {
        if (this.tooltipAnimationRequest) {
            cancelAnimationFrame(this.tooltipAnimationRequest);
            this.tooltipAnimationRequest = undefined;
        }

        if (this.scrollAnimationRequest) {
            cancelAnimationFrame(this.scrollAnimationRequest);
            this.scrollAnimationRequest = undefined;
        }

        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = undefined;
        }
    }

    private hideTooltip() {
        if (this.tooltipTimeout) {
            return;
        }

        this.tooltipTimeout = setTimeout(() => {
            this.tooltipVisible = false;
            this.tooltipTimeout = undefined;
        }, 100);
    }
}
